import { ChangeDetectorRef, Component, ElementRef, HostBinding, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, NEVER, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, finalize, first, map, skip, switchMap, takeUntil } from 'rxjs/operators';
import { StateService } from '@shared/services/state.service';
import { JanusService } from '@shared/services/janus.service';
import { PopupService } from '@shared/services/popup/popup.service';
import { PlatformDetectorService } from '@shared/services/platform-detector/platform-detector.service';
import { SettingsComponent } from '@shared/components/settings/settings.component';
import { MediaService } from '@shared/services/media.service';
import { PopupRef } from '@shared/services/popup/popup.ref';
import { ViewService } from '@shared/services/view/view.service';
import { NotificationService } from '@shared/services/notification/notification.service';
import { AnalyticsService } from '@shared/services/analytics.service';
import { CustomizationService } from '../../services/customization.service';
import { SettingsChanges } from '../../models/settings-changes.model';
import { AuthService } from '@shared/services/auth.service';
import { ConversationService } from '@shared/services/conversation.service';
import { RealtimeDatabaseService } from '../../services/rtdb.service';
import { TokenService } from '@shared/services/token.service';
import { Message, User } from '../../models/message';
import { ConfirmationPopupComponent } from '@shared/components/popups/confirmation-popup/confirmation-popup.component';
import { ensureGlobalObject } from '../../utils/ensure-global-object.util';
import { RecordingService } from '../../services/recording.service';
import { RecordingState } from '../../enums/recording-state';
import { MenuMobileActions } from '../../enums/menu-mobile-actions';
import { CustomizationModel } from '../../models/customization.model';
import { ProfileComponent } from '@shared/components/profile/profile.component';
import { environment } from '../../../../environments/environment';
import firebase from 'firebase/compat';
import { VideoEffectsService } from '@shared/services/video-effects/video-effects.service';
import QuerySnapshot = firebase.firestore.QuerySnapshot;
import { RealtimeDatabaseSubscriptionsService } from '../../services/rtdb-subscriptions.service';
import { PlayLeaveSoundState } from '../../enums/play-leave-sound-state';
import { ControlsService } from '@shared/services/controls.service';

@Component({
  selector: 'stusan-controls',
  templateUrl: './controls.component.html',
  styleUrls: ['./controls.component.scss'],
})
export class ControlsComponent implements OnInit, OnDestroy {
  destroyed$ = new Subject<boolean>();
  loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  notify$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  finishCallEnabled$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public recordingState$: BehaviorSubject<RecordingState> = new BehaviorSubject<RecordingState>(RecordingState.stop);
  public customConfig: CustomizationModel;
  @ViewChild('settingsButton', { read: ElementRef }) settingsButton: ElementRef<any>;
  @ViewChild('menuButton', { read: ElementRef }) menuButton: ElementRef<any>;
  @ViewChild('profileButton', { read: ElementRef }) profileButton: ElementRef<any>;
  public roomUrl: string = window.location.href;
  public isInviteParticipantShown = new BehaviorSubject<boolean>(false);
  public isInviteParticipantSuccessShown = new BehaviorSubject<boolean>(false);
  public isInviteParticipantControlShown$ = new BehaviorSubject<boolean>(false);
  public isMobileView$ = this.viewService.isMobileView$.asObservable();
  public isInviteParticipantSuccessTimer: ReturnType<typeof setTimeout>;

  public cameraMuting$ = this.controlsService.cameraMuting$;
  public micMuting$ = this.controlsService.micMuting$;

  public isDesktopPlatform = false;
  public recordingEnabled = false;

  @HostBinding('class.hidden') isHidden: boolean;
  settings: PopupRef<any> | null;
  profile: PopupRef<any> | null;
  private roomName: string;
  private userId: string | null;
  currentUser: User | null | undefined;
  isVoiceActive = false;

  constructor(
    private router: Router,
    public stateService: StateService,
    public customizationService: CustomizationService,
    public janusService: JanusService,
    private cdr: ChangeDetectorRef,
    private popupService: PopupService,
    private notificationService: NotificationService,
    private media: MediaService,
    private readonly viewService: ViewService,
    private platformDetectorService: PlatformDetectorService,
    public auth: AuthService,
    private readonly conversation: ConversationService,
    private readonly rtdb: RealtimeDatabaseService,
    private readonly tokenService: TokenService,
    private readonly recordingService: RecordingService,
    private readonly videoEffectsService: VideoEffectsService,
    private readonly rtdbSubscriptionsService: RealtimeDatabaseSubscriptionsService,
    private readonly controlsService: ControlsService
  ) {
    this.customConfig = customizationService.config;
  }

  async ngOnInit(): Promise<any> {
    this.currentUser = this.tokenService.currentUser;
    this.isDesktopPlatform = this.platformDetectorService.isDesktop();

    this.stateService.isMicEnabled$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.cdr.markForCheck());

    this.stateService.isCamEnabled$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.cdr.markForCheck());

    this.rtdbSubscriptionsService.localUser$
      .pipe(
        takeUntil(this.destroyed$),
        switchMap((userInfo) => userInfo?.media$ || NEVER),
      )
      .subscribe((media) => {
        // console.log('Local media ' + JSON.stringify(media));
      });

    this.janusService.localStream$
      .subscribe((stream: MediaStream | null) => {
        // console.log('localStream$ voiceActivityDetect', stream);
        this.media.voiceActivityDetect(stream);
      });

    // TODO RTDB better handle controls disabling
    // this.janusService.canSendDc
    //   .pipe(
    //     filter(hasDataChannel => hasDataChannel),
    //     switchMap(() => this.janusService.mutes.pipe(takeUntil(this.destroyed$)))
    //   )
    //   .subscribe((mute: { on: boolean; type: 'audio' | 'video' } | null) => {
    //     if (mute) {
    //       if (mute.type === 'video' || this.stateService.isCamEnabled) {
    //         this.cameraMuting = false;
    //         console.log('sendDCVideoMessage 2')
    //         this.janusService.sendDCVideoMessage();
    //       }
    //
    //       if (mute.type === 'audio' || this.stateService.isMicEnabled) {
    //         this.janusService.sendDCAudioMessage();
    //         this.micMuting = false;
    //       }
    //
    //       this.cdr.detectChanges();
    //     }
    //   });

    if (!environment.signupFlowEnabled) {
      await this.auth.anonymousAuth(this.userName);
    }

    if (this.auth.currentUser) {
      this.notify()
        .pipe(takeUntil(this.destroyed$))
        .subscribe(notify => this.notify$.next(notify));
    }

    this.stateService.isChatShown$
      .asObservable()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isChatShown: boolean) => {
        if (!isChatShown) {
          this.notify$.next(false);
        }
      });

    this.userId = this.stateService.userId;
    this.setRoomName();

    this.tokenService.isHost$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(this.finishCallEnabled$);

    this.stateService.settingsChanges$
      .asObservable()
      .pipe(
        takeUntil(this.destroyed$),
        filter(value => value !== null)
      )
      .subscribe(() => this.saveSettingsChanges(this.media.camera, this.stateService.settingsChanges));

    this.stateService.confirmEndCall$
      .asObservable()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(finishConfirmed => {
        if (finishConfirmed) {
          this.rtdb.set(`rooms/${this.roomName}/hangup`, true);
        }
      });

    this.rtdb
      .subscribe<boolean>(`rooms/${this.roomName}/hangup`)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(hangup => {
        if (hangup) {
          this.finishCall(true);
        }

        if (this.finishCallEnabled$.value) {
          this.rtdb.set(`rooms/${this.roomName}/hangup`, null);
        }
      });

    // If I'm a bot
    this.stateService
      .subscribeOnIsJoinedRoom()
      .pipe(
        filter(joined => joined),
        first()
      )
      .subscribe(async () => {
        // safe timeout so videos will appear
        await new Promise(r => setTimeout(r, 1500));
        // If I'm a bot
        if (this.stateService.isRecordingBot) {
          // confirm that we started recording to backend
          await this.recordingService.confirm(this.roomName).toPromise();
        }
        // mark room ready. Recorder will start capturing only when this function defined
        ensureGlobalObject('APP.conference._room').isJoined = () => true;
      });

    this.isInviteParticipantControlShown$.next(!this.customizationService.config.isAuthEnabled);

    this.rtdbSubscriptionsService.recordingSubscription$
      .pipe(
        takeUntil(this.destroyed$),
        distinctUntilChanged(),
        map(x => x || RecordingState.stop) // can be null in RTDB, means stopped
      )
      .subscribe(this.recordingState$);

    this.stateService.isSettingsShown = false;
    this.stateService.isChatShown = this.customConfig.isChatShown;

    this.isMobileView$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        isMobileView =>
          (this.stateService.isChatShown =
            this.customConfig.isChatShown && !isMobileView)
      );

    this.getLatestMessage()
      .pipe(takeUntil(this.destroyed$), skip(1))
      .subscribe((latestMessage: Message[]) => {
        if (!this.stateService.isChatShown && latestMessage.length) {
          const message = latestMessage[0];

          this.notificationService.showToastr(`${message.user.displayName} wrote: ${message.text}`);
        }
      });

    this.handleVoiceActivity();

    this.stateService.isMobileMenuHidden$
      .asObservable()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(isMobileMenuHidden => {
        this.isHidden = isMobileMenuHidden;
      });
  }

  notify(): Observable<any> {
    let unsubscribe = () => {
    };

    return new Observable(observer => {
      unsubscribe = this.conversation.notify().onSnapshot(
        (snap: QuerySnapshot<any>) => {
          observer.next(!!(snap.docs.map(doc => ({ ...(doc.data() as any) })) as any).length);
        },
        error => {
          console.log(error);
        }
      );
    }).pipe(finalize(unsubscribe));
  }

  getLatestMessage(): Observable<any> {
    let unsubscribe = () => {
    };
    return new Observable(observer => {
      unsubscribe = this.conversation.getLatestMessage().onSnapshot(
        snap => {
          observer.next(
            snap.docs.map(doc => ({
              ...(doc.data() as any),
              user: {
                displayName: doc.data().user.displayName,
              },
            })) as any
          );
        },
        error => {
          console.log(error);
        }
      );
    }).pipe(finalize(unsubscribe));
  }

  handleEndCall() {
    if (this.customConfig.isLikesEnabled) {
      this.auth.logout();
      this.finishCall();
      return;
    }
    if (this.finishCallEnabled$.value) {
      this.openPopup();
    } else {
      this.finishCall();
    }
  }

  public get isAudioEnabled(): boolean {
    return this.stateService.isAudioEnabled;
  }

  public get isVideoEnabled(): boolean {
    return this.stateService.isVideoEnabled;
  }

  public toggleMic(): void {
    this.controlsService.toggleMic();
  }

  public toggleCam(): void {
    this.controlsService.toggleCam();
  }

  public toggleMirror(): void {}

  public async finishCall(redirect?: boolean): Promise<void> {
    if (this.recordingState$.getValue() === RecordingState.recording) {
      const stop = await this.recordingService.stop(this.roomName).toPromise();
      console.log('[rec] stop:', stop);
    }

    // await this.auth.setUserStatus('offline');

    await this.rtdb.update(`rooms/${this.roomName}/users/${this.userId}/isRaisedHand`, false);

    AnalyticsService.configureScope();

    this.stateService.clearState();
    if (this.stateService.playLeaveSound !== PlayLeaveSoundState.notPlay) {
      this.stateService.playLeaveSound = PlayLeaveSoundState.reloadAndPlay; // 2 - reload + play, 1 - play + set to zero
    }
    this.janusService.finishCall();
    let route = ['/'];
    if (redirect) {
      route = ['/finish'];
    }
    this.router.navigate(route).then(() => {
      // TODO: This workaround fix issues after finishing call
      //  In future we need remove this code and handle in proper way the finishing call
      window.location.reload();
    });
  }

  public toggleInviteParticipant(): void {
    if (this.isInviteParticipantShown.value || this.isInviteParticipantSuccessShown.value) {
      this.isInviteParticipantShown.next(false);
    } else {
      this.isInviteParticipantShown.next(true);
    }
    this.isInviteParticipantSuccessShown.next(false);
    clearTimeout(this.isInviteParticipantSuccessTimer);
  }

  public copyRoomUrl(): void {
    navigator.clipboard.writeText(this.roomUrl)
      .then(result => {
        this.isInviteParticipantShown.next(false);
        this.isInviteParticipantSuccessShown.next(true);
        clearTimeout(this.isInviteParticipantSuccessTimer);

        this.isInviteParticipantSuccessTimer = setTimeout(() => {
          this.isInviteParticipantSuccessShown.next(false);
        }, 3000);
      },
      error => {
        this.isInviteParticipantSuccessShown.next(false);
      }
      );
  }

  // public async toggleRaiseHand(): Promise<any> {
  //   const isHandRaised = this.stateService.isHandRaised;
  //   this.stateService.isHandRaised = !isHandRaised;
  //   this.userId = this.stateService.userId;
  //   await this.rtdbSubscriptionsService.toggleRaiseHand(this.userId);
  // }

  private setRoomName(): void {
    this.roomName = this.stateService.roomName as string;
  }

  public toggleChat(): void {
    this.stateService.isParticipantsShown = false;
    this.stateService.isChatShown$.next(!this.stateService.isChatShown$.value);
    this.stateService.isSettingsShown$.next(false);
  }

  public toggleParticipants(): void {
    this.stateService.isChatShown$.next(false);
    this.stateService.isParticipantsShown = !this.stateService.isParticipantsShown;
  }

  public toggleSettings(): void {
    if (this.settings) {
      this.closeSettings();
      this.stateService.isSettingsShown = false;
    } else {
      this.openSettings();
      this.stateService.isSettingsShown = true;
    }
  }

  private openSettings() {
    let origin = this.settingsButton.nativeElement;

    if (this.viewService.isMobileView$.getValue()) {
      origin = this.menuButton.nativeElement;
    }

    this.settings = this.popupService.settings(origin, SettingsComponent, {
      stream: this.media.camera,
      closeByEsc: false
    });

    this.settings.afterClosed$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.settings = null;
        this.stateService.isSettingsShown = !this.stateService.isSettingsShown;
      });
  }

  private saveSettingsChanges(stream: MediaStream | null, settingsChanges: SettingsChanges): void {
    const videoDeviceId = settingsChanges?.videoDeviceId || undefined;
    const audioDeviceId = settingsChanges?.audioDeviceId || undefined;

    this.media.saveConstraints(stream, videoDeviceId, audioDeviceId);
    this.janusService.replaceStream(settingsChanges, true);
  }

  public toggleProfile(): void {
    if (this.settings) {
      this.closeSettings();
      this.stateService.isProfileShown = false;
    } else {
      this.openProfile();
      this.stateService.isProfileShown = true;
    }
  }

  private closeSettings() {
    this.settings?.close();
  }

  private openProfile() {
    const origin = this.profileButton.nativeElement;
    this.profile = this.popupService.settings(origin, ProfileComponent, { closeByEsc: false });
    this.profile.afterClosed$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result: { data: any }) => {
        this.profile = null;
        this.stateService.isProfileShown = !this.stateService.isProfileShown;
      });
  }

  private closeProfile() {
    this.profile?.close();
  }

  private get userName(): string {
    return this.stateService.userName || 'participant';
  }

  onLoginAnonymousClick(event: any) {
    event.stopPropagation();
    this.loading$.next(true);
    this.toggleChat();
    this.auth.anonymousAuth(this.userName).then(authenticated => {
      if (authenticated) {
        this.loading$.next(false);
        this.notify$.next(false);
      }
    });
  }

  openPopup() {
    const dialog = this.popupService.dialog(ConfirmationPopupComponent, {
      message: !this.customConfig.isAuthEnabled ? 'Would you like to end call for everyone in this room?' : 'End the call for everyone?',
      confirmLabel: 'Disconnect All',
      cancelLabel: !this.customConfig.isAuthEnabled ? 'Leave' : 'Cancel',
      closeByClick: true
    });
    const isEscPressed$ = dialog.onKeyDown$.pipe(filter(key => key.key === 'Escape'));
    dialog.afterClosed$
      .pipe(
        takeUntil(this.destroyed$),
        takeUntil(isEscPressed$)
      )
      .subscribe(confirm => {
        if (confirm.type === 'backdropClick' || confirm.data === null) {
          return;
        }
        if (confirm.data) {
          this.stateService.confirmEndCall = true;
        } else {
          if (!this.customConfig.isAuthEnabled) {
            this.finishCall();
          }
        }
      });
  }

  public toggleMobileMenu(): void {
    this.stateService.isMobileMenuShown$.next(!this.stateService.isMobileMenuShown$.value);
  }

  public toogleRecording(): void {
    this.recordingEnabled = !this.recordingEnabled;
  }

  public handleAction(action: MenuMobileActions): void {
    this.toggleMobileMenu();
    switch (action) {
      case MenuMobileActions.settings:
        this.toggleSettings();
        break;
      case MenuMobileActions.invite:
        this.copyRoomUrl();
        this.notificationService.showInfo('Room URL was copied');
        break;
      // case MenuMobileActions.raise:
      //   this.toggleRaiseHand();
      //   break;
      case MenuMobileActions.recording:
        this.toogleRecording();
        break;
    }
  }

  private handleVoiceActivity(): void {
    this.stateService.isVoiceActive$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isVoiceActive: boolean) => this.isVoiceActive = isVoiceActive);
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
